import { useState, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { APP_VERSION } from '../version'
import { CHANGELOG } from '../changelog'
import {
  Settings, Cloud, CheckCircle, XCircle, RefreshCw, Save,
  Eye, EyeOff, Database, Upload, ChevronDown, ChevronUp,
  Play, Clock, Calendar, BookOpen, Headphones, Mail, Send,
  Plus, X as XIcon, Trash2, ShieldCheck, Bot, History, ClipboardCheck, Check,
  DatabaseBackup, HardDriveDownload, HardDriveUpload, AlertTriangle, FileArchive,
  Copy,
} from 'lucide-react'
import MasterDataPage from './MasterDataPage'
import ImportPage from './ImportPage'
import ManualTab from '../components/ManualTab'
import ServiceDeskSettingsPage from './ServiceDeskSettingsPage'

const ic = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono'

interface Holiday { date: string; name: string }

interface CompanyServiceDeskConfig {
  businessHoursStart: number
  businessHoursEnd:   number
  businessDays:       string
  timezone:           string
  holidays:           Holiday[]
}

interface CompanyEmailConfig {
  enabled: boolean
  provider: 'smtp' | 'resend'
  fromEmail: string
  fromName: string
  smtpHost: string
  smtpPort: string
  smtpSecure: boolean
  smtpUser: string
  smtpPassSet: boolean
  resendKeySet: boolean
  notifyTicketOpenedRequester: boolean
  notifyTicketAssigned: boolean
  notifyTicketResolved: boolean
  notifySlaBreach: boolean
  notifyEscalation: boolean
}

interface AiAccess { assets: boolean; tickets: boolean; knowledge: boolean; dashboard: boolean }
type AiDataScope = 'all' | 'self'
interface AiRoleScope { VIEWER: AiDataScope; TECHNICIAN: AiDataScope; ADMIN: AiDataScope }

type AiProvider = 'gemini' | 'claude' | 'openai' | 'groq'
const AI_PROVIDER_OPTIONS: { key: AiProvider; label: string; keyPlaceholder: string; keyHint: ReactNode }[] = [
  { key: 'gemini', label: 'Google Gemini', keyPlaceholder: 'AIzaSy...',
    keyHint: <>รับ API Key ฟรีได้ที่ <span className="font-mono">aistudio.google.com</span></> },
  { key: 'claude', label: 'Anthropic Claude', keyPlaceholder: 'sk-ant-...',
    keyHint: <>รับ API Key ได้ที่ <span className="font-mono">console.anthropic.com</span></> },
  { key: 'openai', label: 'OpenAI ChatGPT', keyPlaceholder: 'sk-...',
    keyHint: <>รับ API Key ได้ที่ <span className="font-mono">platform.openai.com</span></> },
  { key: 'groq', label: 'Groq', keyPlaceholder: 'gsk_...',
    keyHint: <>รับ API Key ฟรีได้ที่ <span className="font-mono">console.groq.com</span></> },
]

interface CompanySamlConfig {
  enabled: boolean
  idpEntityId: string
  idpSsoUrl: string
  idpCertSet: boolean
  spEntityId: string
  acsUrl: string
}

interface CompanyConfig {
  id: string
  name: string
  azure: { tenantId: string; clientId: string; clientSecretSet: boolean }
  saml: CompanySamlConfig
  sync: { enabled: boolean; intervalHours: number; syncTime: string; lastRun: string | null; lastResult: string | null; nextRun: string | null }
  email: CompanyEmailConfig
  servicedesk: CompanyServiceDeskConfig
  ai: {
    provider: AiProvider
    keysSet: Record<AiProvider, boolean>
    companyKeysSet: Record<AiProvider, boolean>
    access: AiAccess
    roleScope: AiRoleScope
  }
}

const SD_DAY_OPTIONS = [
  { label: 'จ',  value: 1 },
  { label: 'อ',  value: 2 },
  { label: 'พ',  value: 3 },
  { label: 'พฤ', value: 4 },
  { label: 'ศ',  value: 5 },
  { label: 'ส',  value: 6 },
  { label: 'อา', value: 7 },
]

const COMMON_TIMEZONES = [
  'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Kolkata',
  'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'UTC',
]

const INTERVAL_OPTIONS = [
  { label: 'ทุก 1 ชั่วโมง',  value: 1   },
  { label: 'ทุก 6 ชั่วโมง',  value: 6   },
  { label: 'ทุก 12 ชั่วโมง', value: 12  },
  { label: 'ทุก 1 วัน',       value: 24  },
  { label: 'ทุก 2 วัน',       value: 48  },
  { label: 'ทุก 1 สัปดาห์',   value: 168 },
]

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('th-TH-u-ca-gregory', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

type SectionKey = 'azure' | 'sso' | 'sync' | 'email' | 'ai' | 'servicedesk'

const SECTION_TABS: { key: SectionKey; label: string; icon: ReactNode }[] = [
  { key: 'azure',       label: 'Azure AD',     icon: <Cloud size={12}/>       },
  { key: 'sso',         label: 'SAML SSO',     icon: <ShieldCheck size={12}/> },
  { key: 'sync',        label: 'Auto Sync',    icon: <RefreshCw size={12}/>   },
  { key: 'email',       label: 'อีเมล์',        icon: <Mail size={12}/>        },
  { key: 'ai',          label: 'IT Assistant', icon: <Bot size={12}/>         },
  { key: 'servicedesk', label: 'ServiceDesk',  icon: <Headphones size={12}/> },
]

// ─── Per-Company Azure + Sync Card ────────────────────────────────────────────
function CompanyConnectionCard({ company }: { company: CompanyConfig }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<SectionKey>('azure')
  const [showSecret, setShowSecret] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [azureForm, setAzureForm] = useState({
    tenantId: company.azure.tenantId,
    clientId: company.azure.clientId,
    clientSecret: '',
  })
  const [syncForm, setSyncForm] = useState({
    enabled: company.sync.enabled,
    intervalHours: company.sync.intervalHours,
    syncTime: company.sync.syncTime || '02:00',
  })
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)

  const [emailForm, setEmailForm] = useState({
    enabled:              company.email?.enabled ?? false,
    provider:             (company.email?.provider || 'smtp') as 'smtp' | 'resend',
    fromEmail:            company.email?.fromEmail || '',
    fromName:             company.email?.fromName || 'IT Management',
    smtpHost:             company.email?.smtpHost || '',
    smtpPort:             company.email?.smtpPort || '587',
    smtpSecure:           company.email?.smtpSecure ?? false,
    smtpUser:             company.email?.smtpUser || '',
    smtpPass:             '',
    resendKey:            '',
    notifyTicketOpenedRequester: company.email?.notifyTicketOpenedRequester ?? true,
    notifyTicketAssigned: company.email?.notifyTicketAssigned ?? true,
    notifyTicketResolved: company.email?.notifyTicketResolved ?? true,
    notifySlaBreach:      company.email?.notifySlaBreach ?? true,
    notifyEscalation:     company.email?.notifyEscalation ?? true,
  })
  const [showSmtpPass, setShowSmtpPass] = useState(false)
  const [showResendKey, setShowResendKey] = useState(false)
  const [testEmailTo, setTestEmailTo] = useState('')
  const [testingEmail, setTestingEmail] = useState(false)
  const [emailTestResult, setEmailTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const [aiProvider, setAiProvider] = useState<AiProvider>(company.ai?.provider || 'gemini')
  const [aiApiKey, setAiApiKey] = useState('')
  const [showAiKey, setShowAiKey] = useState(false)
  const [savingAi, setSavingAi] = useState(false)
  const [testingAi, setTestingAi] = useState(false)
  const [aiResult, setAiResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [aiAccessForm, setAiAccessForm] = useState<AiAccess>({
    assets:    company.ai?.access?.assets    ?? true,
    tickets:   company.ai?.access?.tickets   ?? true,
    knowledge: company.ai?.access?.knowledge ?? true,
    dashboard: company.ai?.access?.dashboard ?? true,
  })
  const saveAiAccessMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/ai-access`, aiAccessForm),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings-companies'] }),
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })
  const [aiRoleScopeForm, setAiRoleScopeForm] = useState<AiRoleScope>({
    VIEWER:     company.ai?.roleScope?.VIEWER     ?? 'self',
    TECHNICIAN: company.ai?.roleScope?.TECHNICIAN ?? 'all',
    ADMIN:      company.ai?.roleScope?.ADMIN      ?? 'all',
  })
  const saveAiRoleScopeMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/ai-role-scope`, aiRoleScopeForm),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings-companies'] }),
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const [sdForm, setSdForm] = useState({
    businessHoursStart: company.servicedesk?.businessHoursStart ?? 8,
    businessHoursEnd:   company.servicedesk?.businessHoursEnd   ?? 17,
    businessDays:       company.servicedesk?.businessDays       || '1,2,3,4,5',
    timezone:           company.servicedesk?.timezone            || 'Asia/Bangkok',
    holidays:           (company.servicedesk?.holidays          ?? []) as Holiday[],
  })
  const [newHolidayDate, setNewHolidayDate] = useState('')
  const [newHolidayName, setNewHolidayName] = useState('')

  const saveSdMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/servicedesk`, sdForm),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings-companies'] }),
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const toggleDay = (dayNum: number) => {
    setSdForm(f => {
      const current = f.businessDays.split(',').filter(Boolean).map(Number)
      const next = current.includes(dayNum)
        ? current.filter(d => d !== dayNum)
        : [...current, dayNum].sort((a, b) => a - b)
      return { ...f, businessDays: next.join(',') }
    })
  }

  const addHoliday = () => {
    if (!newHolidayDate) return
    setSdForm(f => ({
      ...f,
      holidays: [...f.holidays.filter(h => h.date !== newHolidayDate), { date: newHolidayDate, name: newHolidayName }]
        .sort((a, b) => a.date.localeCompare(b.date)),
    }))
    setNewHolidayDate('')
    setNewHolidayName('')
  }

  const removeHoliday = (date: string) => {
    setSdForm(f => ({ ...f, holidays: f.holidays.filter(h => h.date !== date) }))
  }

  const saveEmailMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/email`, emailForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings-companies'] }); setEmailTestResult(null) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const handleTestEmail = async () => {
    if (!testEmailTo) return
    setTestingEmail(true); setEmailTestResult(null)
    try {
      const res = await api.post(`/settings/companies/${company.id}/email/test`, {
        testTo: testEmailTo,
        ...emailForm,
      })
      setEmailTestResult({ ok: true, message: res.data.message })
    } catch (e: any) {
      setEmailTestResult({ ok: false, message: e.response?.data?.message || 'ส่งไม่สำเร็จ' })
    } finally { setTestingEmail(false) }
  }

  const handleSaveAi = async () => {
    setSavingAi(true); setAiResult(null)
    try {
      await api.put(`/settings/companies/${company.id}/ai`, { provider: aiProvider, apiKey: aiApiKey })
      qc.invalidateQueries({ queryKey: ['settings-companies'] })
      setAiApiKey('')
      setAiResult({ ok: true, message: 'บันทึกสำเร็จ' })
    } catch (e: any) {
      setAiResult({ ok: false, message: e.response?.data?.message || 'เกิดข้อผิดพลาด' })
    } finally { setSavingAi(false) }
  }

  const handleTestAi = async () => {
    setTestingAi(true); setAiResult(null)
    try {
      const res = await api.post(`/settings/companies/${company.id}/ai/test`, { provider: aiProvider, apiKey: aiApiKey || undefined })
      setAiResult({ ok: true, message: res.data.message })
    } catch (e: any) {
      setAiResult({ ok: false, message: e.response?.data?.message || 'ทดสอบไม่สำเร็จ' })
    } finally { setTestingAi(false) }
  }

  const [samlForm, setSamlForm] = useState({
    enabled:     company.saml?.enabled ?? false,
    idpEntityId: company.saml?.idpEntityId || '',
    idpSsoUrl:   company.saml?.idpSsoUrl || '',
    idpCert:     '',
  })
  const [samlCopied, setSamlCopied] = useState<string | null>(null)

  const samlConfigured = !!(company.saml?.idpEntityId && company.saml?.idpSsoUrl && company.saml?.idpCertSet)

  const saveSamlMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/saml`, {
      enabled:     samlForm.enabled,
      idpEntityId: samlForm.idpEntityId,
      idpSsoUrl:   samlForm.idpSsoUrl,
      idpCert:     samlForm.idpCert || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings-companies'] }); setSamlForm(f => ({ ...f, idpCert: '' })) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const copySamlValue = (label: string, value: string) => {
    navigator.clipboard?.writeText(value)
    setSamlCopied(label)
    setTimeout(() => setSamlCopied(c => c === label ? null : c), 1500)
  }

  const configured = !!(company.azure.tenantId && company.azure.clientId && company.azure.clientSecretSet)

  const saveMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/azure`, {
      tenantId: azureForm.tenantId,
      clientId: azureForm.clientId,
      clientSecret: azureForm.clientSecret || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings-companies'] }); setTestResult(null) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const saveSyncMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${company.id}/sync`, {
      ...syncForm,
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings-companies'] }),
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const handleTest = async () => {
    setTesting(true); setTestResult(null)
    try {
      await api.post(`/settings/companies/${company.id}/azure/test`, {})
      setTestResult({ ok: true, message: 'เชื่อมต่อสำเร็จ' })
    } catch (e: any) {
      setTestResult({ ok: false, message: e.response?.data?.message || 'ไม่สามารถเชื่อมต่อได้' })
    } finally { setTesting(false) }
  }

  const handleSyncNow = async () => {
    setSyncing(true); setSyncResult(null)
    try {
      const res = await api.post(`/settings/companies/${company.id}/sync/run`, {})
      setSyncResult(res.data.message)
      qc.invalidateQueries({ queryKey: ['settings-companies'] })
    } catch (e: any) {
      setSyncResult(`ผิดพลาด: ${e.response?.data?.message || e.message}`)
    } finally { setSyncing(false) }
  }

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-5 hover:bg-gray-50 transition-colors text-left">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${configured ? 'bg-indigo-100' : 'bg-gray-100'}`}>
          <Cloud size={17} className={configured ? 'text-indigo-600' : 'text-gray-400'}/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{company.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {configured ? 'Azure AD ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้งค่า Azure AD'}
            {company.sync.enabled && company.sync.nextRun && (
              <span className="ml-2 text-blue-500">· ซิงค์ถัดไป {fmtDate(company.sync.nextRun)}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {configured ? 'ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้งค่า'}
          </span>
          {company.sync.enabled && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium flex items-center gap-1">
              <Clock size={10}/> Auto Sync
            </span>
          )}
          {company.saml?.enabled && samlConfigured && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-1">
              <ShieldCheck size={10}/> SSO
            </span>
          )}
          {open ? <ChevronUp size={15} className="text-gray-400"/> : <ChevronDown size={15} className="text-gray-400"/>}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {/* Section Tab Bar */}
          <div className="flex border-b border-gray-100 px-5 gap-1 overflow-x-auto">
            {SECTION_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-5">
          {/* Azure AD Config */}
          {activeSection === 'azure' && <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Azure AD / Microsoft 365</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tenant ID <span className="text-gray-400 font-normal">(Directory ID)</span></label>
                <input value={azureForm.tenantId} onChange={e => setAzureForm(f => ({ ...f, tenantId: e.target.value }))} className={ic} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client ID <span className="text-gray-400 font-normal">(Application ID)</span></label>
                <input value={azureForm.clientId} onChange={e => setAzureForm(f => ({ ...f, clientId: e.target.value }))} className={ic} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Client Secret
                  {company.azure.clientSecretSet && <span className="ml-1.5 text-green-600 font-normal">● บันทึกไว้แล้ว</span>}
                </label>
                <div className="relative">
                  <input value={azureForm.clientSecret} onChange={e => setAzureForm(f => ({ ...f, clientSecret: e.target.value }))}
                    type={showSecret ? 'text' : 'password'} className={ic + ' pr-10'}
                    placeholder={company.azure.clientSecretSet ? '••••••••  (เว้นว่างถ้าไม่เปลี่ยน)' : 'Secret Value จาก Azure Portal'}/>
                  <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showSecret ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saveMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึก
                </button>
                <button onClick={handleTest} disabled={testing || !configured}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
                  {testing ? <RefreshCw size={13} className="animate-spin"/> : <Cloud size={13}/>} ทดสอบ
                </button>
                {testResult && (
                  <span className={`flex items-center gap-1.5 text-xs font-medium ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                    {testResult.ok ? <CheckCircle size={13}/> : <XCircle size={13}/>} {testResult.message}
                  </span>
                )}
              </div>
            </div>
          </div>}

          {/* SAML SSO Config */}
          {activeSection === 'sso' && <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">SAML Single Sign-On (Microsoft Entra ID)</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-800">เปิดใช้งาน SAML SSO</p>
                  <p className="text-xs text-gray-400 mt-0.5">เป็นทางเลือกเสริมนอกเหนือจาก username/password เดิม — ผู้ใช้ต้องมีบัญชีอยู่ในระบบแล้ว (จับคู่ด้วยอีเมล)</p>
                </div>
                <button onClick={() => setSamlForm(f => ({ ...f, enabled: !f.enabled }))}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${samlForm.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${samlForm.enabled ? 'translate-x-5' : ''}`}/>
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">IdP Entity ID <span className="text-gray-400 font-normal">(Azure AD Identifier)</span></label>
                <input value={samlForm.idpEntityId} onChange={e => setSamlForm(f => ({ ...f, idpEntityId: e.target.value }))} className={ic} placeholder="https://sts.windows.net/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx/"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">IdP SSO URL <span className="text-gray-400 font-normal">(Login URL)</span></label>
                <input value={samlForm.idpSsoUrl} onChange={e => setSamlForm(f => ({ ...f, idpSsoUrl: e.target.value }))} className={ic} placeholder="https://login.microsoftonline.com/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx/saml2"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  IdP Certificate (Base64)
                  {company.saml?.idpCertSet && <span className="ml-1.5 text-green-600 font-normal">● บันทึกไว้แล้ว</span>}
                </label>
                <textarea value={samlForm.idpCert} onChange={e => setSamlForm(f => ({ ...f, idpCert: e.target.value }))}
                  rows={4} className={ic + ' resize-none'}
                  placeholder={company.saml?.idpCertSet ? '(เว้นว่างถ้าไม่เปลี่ยน)' : 'วางค่า Certificate (Base64) จาก Azure Portal'}/>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button onClick={() => saveSamlMut.mutate()} disabled={saveSamlMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saveSamlMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึก
                </button>
                {samlConfigured ? (
                  <a href={`/api/auth/saml/${company.id}/login`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
                    <ShieldCheck size={13}/> ทดสอบเข้าสู่ระบบ
                  </a>
                ) : (
                  <span className="flex items-center gap-2 px-4 py-2 border border-gray-100 text-gray-300 rounded-xl text-sm font-medium cursor-not-allowed">
                    <ShieldCheck size={13}/> ทดสอบเข้าสู่ระบบ
                  </span>
                )}
              </div>

              <div className="border-t border-gray-100 pt-3 mt-1">
                <p className="text-xs font-medium text-gray-500 mb-2">ค่าที่ต้องตั้งใน Microsoft Entra ID (Enterprise App → Single sign-on → SAML → Basic SAML Configuration)</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Identifier (Entity ID)</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2 truncate">{company.saml?.spEntityId}</code>
                      <button type="button" onClick={() => copySamlValue('entity', company.saml.spEntityId)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex-shrink-0" title="คัดลอก">
                        {samlCopied === 'entity' ? <Check size={13} className="text-green-600"/> : <Copy size={13}/>}
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Reply URL (ACS URL)</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2 truncate">{company.saml?.acsUrl}</code>
                      <button type="button" onClick={() => copySamlValue('acs', company.saml.acsUrl)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex-shrink-0" title="คัดลอก">
                        {samlCopied === 'acs' ? <Check size={13} className="text-green-600"/> : <Copy size={13}/>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

          {/* Sync Schedule */}
          {activeSection === 'sync' && <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Calendar size={12}/> ตั้งเวลาซิงค์ User อัตโนมัติ
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-800">เปิดใช้งานซิงค์อัตโนมัติ</p>
                  <p className="text-xs text-gray-400 mt-0.5">ดึง user จาก Azure AD ตามรอบที่กำหนด</p>
                </div>
                <button onClick={() => setSyncForm(f => ({ ...f, enabled: !f.enabled }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${syncForm.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${syncForm.enabled ? 'translate-x-5' : ''}`}/>
                </button>
              </div>

              {syncForm.enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">รอบการทำงาน</label>
                    <select value={syncForm.intervalHours} onChange={e => setSyncForm(f => ({ ...f, intervalHours: parseInt(e.target.value) }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                      {INTERVAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      เวลาที่รัน
                      {syncForm.intervalHours < 24 && <span className="ml-1 text-gray-400 font-normal">(ใช้กับ ≥ 1 วัน)</span>}
                    </label>
                    <input type="time" value={syncForm.syncTime}
                      onChange={e => setSyncForm(f => ({ ...f, syncTime: e.target.value }))}
                      disabled={syncForm.intervalHours < 24}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-40 disabled:cursor-not-allowed"/>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button onClick={() => saveSyncMut.mutate()} disabled={saveSyncMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                  {saveSyncMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกตาราง
                </button>
                <button onClick={handleSyncNow} disabled={syncing || !configured}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
                  {syncing ? <RefreshCw size={13} className="animate-spin"/> : <Play size={13}/>} ซิงค์ตอนนี้
                </button>
              </div>

              {syncResult && (
                <p className={`text-xs px-3 py-2 rounded-lg ${syncResult.startsWith('ผิดพลาด') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  {syncResult}
                </p>
              )}

              {/* Status info */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-gray-400 mb-0.5">ซิงค์ล่าสุด</p>
                  <p className="font-medium text-gray-700">{fmtDate(company.sync.lastRun)}</p>
                  {company.sync.lastResult && <p className="text-gray-400 mt-0.5 truncate">{company.sync.lastResult}</p>}
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-gray-400 mb-0.5">ซิงค์ครั้งถัดไป</p>
                  <p className="font-medium text-gray-700">{company.sync.enabled ? fmtDate(company.sync.nextRun) : 'ปิดใช้งาน'}</p>
                </div>
              </div>
            </div>
          </div>}

          {/* Email Notifications */}
          {activeSection === 'email' && <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Mail size={12}/> การแจ้งเตือนทางเมล์
            </h4>
            <div className="space-y-4">
              {/* Enable toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-800">เปิดใช้งานการแจ้งเตือนทางเมล์</p>
                  <p className="text-xs text-gray-400 mt-0.5">ส่งเมล์แจ้งเตือนเมื่อมีเหตุการณ์ใน Ticket</p>
                </div>
                <button onClick={() => setEmailForm(f => ({ ...f, enabled: !f.enabled }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${emailForm.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${emailForm.enabled ? 'translate-x-5' : ''}`}/>
                </button>
              </div>

              {emailForm.enabled && (<>
                {/* Provider */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">ผู้ให้บริการ</label>
                  <div className="flex gap-2">
                    {(['smtp', 'resend'] as const).map(p => (
                      <button key={p} onClick={() => setEmailForm(f => ({ ...f, provider: p }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${emailForm.provider === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                        {p === 'smtp' ? 'SMTP' : 'Resend'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* From */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">ชื่อผู้ส่ง</label>
                    <input value={emailForm.fromName} onChange={e => setEmailForm(f => ({ ...f, fromName: e.target.value }))}
                      className={ic} placeholder="IT Management"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">อีเมลผู้ส่ง</label>
                    <input value={emailForm.fromEmail} onChange={e => setEmailForm(f => ({ ...f, fromEmail: e.target.value }))}
                      className={ic} placeholder="noreply@company.com"/>
                  </div>
                </div>

                {/* SMTP fields */}
                {emailForm.provider === 'smtp' && (
                  <div className="space-y-3 p-3 bg-gray-50 rounded-xl">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">SMTP Host</label>
                        <input value={emailForm.smtpHost} onChange={e => setEmailForm(f => ({ ...f, smtpHost: e.target.value }))}
                          className={ic} placeholder="smtp.company.com"/>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Port</label>
                        <input value={emailForm.smtpPort} onChange={e => setEmailForm(f => ({ ...f, smtpPort: e.target.value }))}
                          className={ic} placeholder="587"/>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id={`ssl-${company.id}`} checked={emailForm.smtpSecure}
                        onChange={e => setEmailForm(f => ({
                          ...f,
                          smtpSecure: e.target.checked,
                          smtpPort: e.target.checked ? '465' : (f.smtpPort === '465' ? '587' : f.smtpPort),
                        }))}
                        className="rounded border-gray-300 text-blue-600"/>
                      <label htmlFor={`ssl-${company.id}`} className="text-xs text-gray-600">
                        ใช้ SSL/TLS <span className="text-gray-400">(port 465)</span>
                      </label>
                    </div>
                    {emailForm.smtpSecure && emailForm.smtpPort !== '465' && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                        <span className="mt-0.5 flex-shrink-0">⚠️</span>
                        <span>
                          SSL/TLS ต้องใช้ <strong>port 465</strong> แต่ตอนนี้ตั้งไว้ที่ port {emailForm.smtpPort}<br/>
                          สำหรับ port 587 ให้ <strong>ยกเลิก checkbox SSL/TLS</strong> (ใช้ STARTTLS แทน)
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">ชื่อผู้ใช้</label>
                        <input value={emailForm.smtpUser} onChange={e => setEmailForm(f => ({ ...f, smtpUser: e.target.value }))}
                          className={ic} placeholder="user@company.com"/>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          รหัสผ่าน
                          {company.email?.smtpPassSet && <span className="ml-1.5 text-green-600 font-normal">● บันทึกไว้แล้ว</span>}
                        </label>
                        <div className="relative">
                          <input value={emailForm.smtpPass} onChange={e => setEmailForm(f => ({ ...f, smtpPass: e.target.value }))}
                            type={showSmtpPass ? 'text' : 'password'} className={ic + ' pr-10'}
                            placeholder={company.email?.smtpPassSet ? '••••••••' : 'รหัสผ่าน SMTP'}/>
                          <button type="button" onClick={() => setShowSmtpPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            {showSmtpPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Resend API key */}
                {emailForm.provider === 'resend' && (
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Resend API Key
                      {company.email?.resendKeySet && <span className="ml-1.5 text-green-600 font-normal">● บันทึกไว้แล้ว</span>}
                    </label>
                    <div className="relative">
                      <input value={emailForm.resendKey} onChange={e => setEmailForm(f => ({ ...f, resendKey: e.target.value }))}
                        type={showResendKey ? 'text' : 'password'} className={ic + ' pr-10'}
                        placeholder={company.email?.resendKeySet ? '••••••••' : 're_xxxxxxxx'}/>
                      <button type="button" onClick={() => setShowResendKey(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showResendKey ? <EyeOff size={15}/> : <Eye size={15}/>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Notification toggles */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">เหตุการณ์ที่แจ้งเตือน</label>
                  <div className="space-y-2">
                    {([
                      { key: 'notifyTicketOpenedRequester', label: 'เปิด Ticket ใหม่ (ผู้เปิด)', desc: 'แจ้ง Requester ว่าได้รับเรื่องแล้ว พร้อม Ticket number' },
                      { key: 'notifyTicketAssigned', label: 'มอบหมาย Ticket',           desc: 'แจ้ง Assignee เมื่อได้รับหรือถูกเปลี่ยน Assignee' },
                      { key: 'notifyTicketResolved', label: 'แก้ไข Ticket แล้ว',        desc: 'แจ้ง Requester เมื่อ Ticket ได้รับการแก้ไข' },
                      { key: 'notifySlaBreach',      label: 'SLA Breach',               desc: 'แจ้งเมื่อ Ticket เกิน SLA' },
                      { key: 'notifyEscalation',     label: 'Escalation',               desc: 'แจ้ง Admin เมื่อ Ticket ถูก Escalate' },
                    ] as const).map(item => (
                      <label key={item.key} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={emailForm[item.key]}
                          onChange={e => setEmailForm(f => ({ ...f, [item.key]: e.target.checked }))}
                          className="rounded border-gray-300 text-blue-600 w-4 h-4 flex-shrink-0"/>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{item.label}</p>
                          <p className="text-xs text-gray-400">{item.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Save button */}
                <button onClick={() => saveEmailMut.mutate()} disabled={saveEmailMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saveEmailMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกการตั้งค่าเมล์
                </button>

                {/* Test email */}
                <div className="border-t border-gray-100 pt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">ทดสอบส่งเมล์</label>
                  <div className="flex gap-2">
                    <input value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)}
                      className={ic + ' flex-1'} placeholder="อีเมลที่ต้องการทดสอบ"/>
                    <button onClick={handleTestEmail} disabled={testingEmail || !testEmailTo}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap">
                      {testingEmail ? <RefreshCw size={13} className="animate-spin"/> : <Send size={13}/>} ส่งทดสอบ
                    </button>
                  </div>
                  {emailTestResult && (
                    <p className={`text-xs mt-2 flex items-center gap-1.5 ${emailTestResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                      {emailTestResult.ok ? <CheckCircle size={13}/> : <XCircle size={13}/>} {emailTestResult.message}
                    </p>
                  )}
                </div>
              </>)}

              {/* Save when disabled too (to turn off) */}
              {!emailForm.enabled && company.email?.enabled && (
                <button onClick={() => saveEmailMut.mutate()} disabled={saveEmailMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-xl text-sm font-medium hover:bg-gray-700 disabled:opacity-50">
                  {saveEmailMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึก (ปิดการแจ้งเตือน)
                </button>
              )}
            </div>
          </div>}

          {/* AI Assistant */}
          {activeSection === 'ai' && <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Bot size={12}/> IT Assistant
            </h4>
            <div className="space-y-3">
              {/* Provider selector */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">ผู้ให้บริการ AI</label>
                <div className="grid grid-cols-2 gap-2">
                  {AI_PROVIDER_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => { setAiProvider(opt.key); setAiApiKey(''); setAiResult(null) }}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors text-center ${
                        aiProvider === opt.key
                          ? 'bg-violet-600 border-violet-600 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                      {company.ai?.provider === opt.key && (
                        <span className={`block text-[10px] font-normal mt-0.5 ${aiProvider === opt.key ? 'text-violet-100' : 'text-gray-400'}`}>ใช้งานอยู่</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                {company.ai?.keysSet?.[aiProvider] ? (
                  <>
                    <CheckCircle size={14} className="text-green-500 flex-shrink-0"/>
                    <div>
                      <p className="text-sm font-medium text-gray-800">พร้อมใช้งาน</p>
                      <p className="text-xs text-gray-400">
                        {company.ai?.companyKeysSet?.[aiProvider] ? 'ใช้ API Key ของบริษัทนี้' : 'ใช้ API Key จาก Environment Variable'}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircle size={14} className="text-red-400 flex-shrink-0"/>
                    <div>
                      <p className="text-sm font-medium text-gray-800">ยังไม่ได้ตั้งค่า</p>
                      <p className="text-xs text-gray-400">ต้องใส่ {AI_PROVIDER_OPTIONS.find(o => o.key === aiProvider)?.label} API Key เพื่อใช้งาน IT Assistant</p>
                    </div>
                  </>
                )}
              </div>

              {/* API Key input */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {AI_PROVIDER_OPTIONS.find(o => o.key === aiProvider)?.label} API Key
                  {company.ai?.companyKeysSet?.[aiProvider] && <span className="ml-1.5 text-green-600 font-normal">● บันทึกไว้แล้ว</span>}
                </label>
                <div className="relative">
                  <input
                    value={aiApiKey}
                    onChange={e => { setAiApiKey(e.target.value); setAiResult(null) }}
                    type={showAiKey ? 'text' : 'password'}
                    className={ic + ' pr-10'}
                    placeholder={company.ai?.companyKeysSet?.[aiProvider] ? '••••••••' : AI_PROVIDER_OPTIONS.find(o => o.key === aiProvider)?.keyPlaceholder}
                  />
                  <button type="button" onClick={() => setShowAiKey(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showAiKey ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {AI_PROVIDER_OPTIONS.find(o => o.key === aiProvider)?.keyHint}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button onClick={handleSaveAi} disabled={savingAi || (!aiApiKey && aiProvider === company.ai?.provider)}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                  {savingAi ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึก
                </button>
                <button onClick={handleTestAi} disabled={testingAi || (!aiApiKey && !company.ai?.keysSet?.[aiProvider])}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
                  {testingAi ? <RefreshCw size={13} className="animate-spin"/> : <Play size={13}/>} ทดสอบ
                </button>
                {company.ai?.companyKeysSet?.[aiProvider] && (
                  <button
                    onClick={async () => {
                      await api.put(`/settings/companies/${company.id}/ai`, { provider: aiProvider, apiKey: '' })
                      qc.invalidateQueries({ queryKey: ['settings-companies'] })
                      setAiApiKey(''); setAiResult(null)
                    }}
                    className="ml-auto flex items-center gap-1.5 px-3 py-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl text-xs transition-colors">
                    <XIcon size={12}/> ลบ Key
                  </button>
                )}
              </div>

              {aiResult && (
                <p className={`text-xs flex items-center gap-1.5 ${aiResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                  {aiResult.ok ? <CheckCircle size={13}/> : <XCircle size={13}/>} {aiResult.message}
                </p>
              )}

              {/* Data Access Control */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <ShieldCheck size={12}/> ข้อมูลที่ AI เข้าถึงได้
                  </p>
                  <p className="text-xs text-gray-400 mb-3">เลือกว่า IT Assistant จะสามารถค้นหาหรือดูข้อมูลส่วนใดได้บ้าง</p>
                </div>
                <div className="space-y-2">
                  {([
                    { key: 'assets'    as const, label: 'Assets',             desc: 'ค้นหาข้อมูลอุปกรณ์ IT เช่น คอมพิวเตอร์ จอ เครื่องพิมพ์' },
                    { key: 'tickets'   as const, label: 'ServiceDesk Tickets', desc: 'ค้นหา Ticket การแจ้งปัญหาและขอรับบริการ' },
                    { key: 'knowledge' as const, label: 'Knowledge Base',      desc: 'ค้นหาบทความ คู่มือ และ FAQ' },
                    { key: 'dashboard' as const, label: 'Dashboard Summary',   desc: 'ดูสรุปสถิติภาพรวมของระบบ' },
                  ]).map(item => (
                    <label key={item.key} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{item.label}</p>
                        <p className="text-xs text-gray-400">{item.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiAccessForm(f => ({ ...f, [item.key]: !f[item.key] }))}
                        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${aiAccessForm[item.key] ? 'bg-blue-600' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${aiAccessForm[item.key] ? 'translate-x-5' : ''}`}/>
                      </button>
                    </label>
                  ))}
                </div>
                <button onClick={() => saveAiAccessMut.mutate()} disabled={saveAiAccessMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-xl text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                  {saveAiAccessMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกการเข้าถึงข้อมูล
                </button>
              </div>

              {/* Role-based Data Scope */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <ShieldCheck size={12}/> ขอบเขตข้อมูลตาม Role
                  </p>
                  <p className="text-xs text-gray-400 mb-3">กำหนดว่าแต่ละ Role จะเห็นข้อมูลทั้งหมดของบริษัท หรือเฉพาะของตัวเอง (Assets ที่ครอบครอง / Tickets ที่เปิดเอง)</p>
                </div>
                <div className="space-y-2">
                  {([
                    { role: 'VIEWER'     as const, label: 'VIEWER',     desc: 'พนักงานทั่วไป' },
                    { role: 'TECHNICIAN' as const, label: 'TECHNICIAN', desc: 'เจ้าหน้าที่ IT' },
                    { role: 'ADMIN'      as const, label: 'ADMIN',      desc: 'ผู้ดูแลระบบ' },
                  ]).map(item => (
                    <div key={item.role} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{item.label} <span className="text-xs text-gray-400 font-normal">— {item.desc}</span></p>
                      </div>
                      <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5">
                        <button
                          onClick={() => setAiRoleScopeForm(f => ({ ...f, [item.role]: 'self' }))}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${aiRoleScopeForm[item.role] === 'self' ? 'bg-amber-500 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                        >เฉพาะของตัวเอง</button>
                        <button
                          onClick={() => setAiRoleScopeForm(f => ({ ...f, [item.role]: 'all' }))}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${aiRoleScopeForm[item.role] === 'all' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                        >ทั้งหมด</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => saveAiRoleScopeMut.mutate()} disabled={saveAiRoleScopeMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-xl text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                  {saveAiRoleScopeMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกขอบเขตตาม Role
                </button>
              </div>
            </div>
          </div>}

          {/* ServiceDesk Business Hours */}
          {activeSection === 'servicedesk' && <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Headphones size={12}/> ServiceDesk — เวลาทำการ &amp; วันหยุด
            </h4>
            <div className="space-y-4">
              {/* Business Hours */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">เวลาเริ่มงาน</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="23"
                      value={sdForm.businessHoursStart}
                      onChange={e => setSdForm(f => ({ ...f, businessHoursStart: parseInt(e.target.value) || 0 }))}
                      className="w-16 border border-gray-200 rounded-lg px-2 py-2.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    <span className="text-sm text-gray-500">:00 น.</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">เวลาเลิกงาน</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" max="24"
                      value={sdForm.businessHoursEnd}
                      onChange={e => setSdForm(f => ({ ...f, businessHoursEnd: parseInt(e.target.value) || 17 }))}
                      className="w-16 border border-gray-200 rounded-lg px-2 py-2.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    <span className="text-sm text-gray-500">:00 น.</span>
                  </div>
                </div>
              </div>

              {/* Business Days */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">วันทำงาน</label>
                <div className="flex gap-1.5">
                  {SD_DAY_OPTIONS.map(d => {
                    const active = sdForm.businessDays.split(',').filter(Boolean).includes(String(d.value))
                    return (
                      <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                        className={`w-9 h-9 rounded-lg text-xs font-semibold transition-colors ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {d.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
                <select value={sdForm.timezone}
                  onChange={e => setSdForm(f => ({ ...f, timezone: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>

              {/* Save business hours */}
              <button onClick={() => saveSdMut.mutate()} disabled={saveSdMut.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                {saveSdMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกเวลาทำการ
              </button>

              {/* Holidays */}
              <div className="border-t border-gray-100 pt-4">
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Calendar size={12}/> วันหยุด
                </h5>

                {/* Add holiday form */}
                <div className="flex gap-2 mb-3">
                  <input type="date" value={newHolidayDate}
                    onChange={e => setNewHolidayDate(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  <input value={newHolidayName} onChange={e => setNewHolidayName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addHoliday()}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ชื่อวันหยุด เช่น วันสงกรานต์..."/>
                  <button onClick={addHoliday} disabled={!newHolidayDate}
                    className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-40">
                    <Plus size={14}/>
                  </button>
                </div>

                {/* Holiday list */}
                {sdForm.holidays.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-lg">
                    ยังไม่มีวันหยุด
                  </p>
                ) : (
                  <div className="space-y-1.5 mb-3">
                    {sdForm.holidays.map(h => (
                      <div key={h.date} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-500 w-24 flex-shrink-0">{h.date}</span>
                          <span className="text-sm text-gray-800">{h.name || '—'}</span>
                        </div>
                        <button onClick={() => removeHoliday(h.date)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600">
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {sdForm.holidays.length > 0 && (
                  <button onClick={() => saveSdMut.mutate()} disabled={saveSdMut.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {saveSdMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกวันหยุด
                  </button>
                )}
              </div>
            </div>
          </div>}

          </div>{/* end p-5 */}
        </div>
      )}
    </div>
  )
}

// ─── Menu Access Tab ─────────────────────────────────────────────────────────
const ALL_MENUS = [
  { key: 'dashboard',  label: 'Dashboard' },
  { key: 'tickets',    label: 'ServiceDesk' },
  { key: 'assets',     label: 'Assets' },
  { key: 'software',   label: 'Software & License' },
  { key: 'deliveries', label: 'ใบส่งมอบ' },
  { key: 'workorders', label: 'ใบงาน' },
  { key: 'dailycheck', label: 'System Check' },
  { key: 'bookings',   label: 'การยืม (แท็บใน Assets)' },
  { key: 'networkdiagram', label: 'Network Diagram (แท็บใน Assets)' },
  { key: 'reports',    label: 'รายงาน (แท็บใน Dashboard)' },
  { key: 'knowledge',  label: 'Knowledge Base' },
  { key: 'chatbot',    label: 'IT Assistant' },
  { key: 'zabbix',     label: 'Monitor AI' },
]
const CONFIGURABLE_ROLES = ['VIEWER', 'TECHNICIAN']

function MenuAccessTab() {
  const qc = useQueryClient()
  const { data: companies = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['menu-companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data),
  })

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')

  useEffect(() => {
    if (companies.length > 0 && !selectedCompanyId) setSelectedCompanyId(companies[0].id)
  }, [companies, selectedCompanyId])

  const { data: menuCfg, isLoading } = useQuery<{ allMenus: string[]; access: Record<string, string[]> }>({
    queryKey: ['menu-access', selectedCompanyId],
    queryFn: () => api.get(`/settings/companies/${selectedCompanyId}/menu-access`).then(r => r.data),
    enabled: !!selectedCompanyId,
  })

  const [localAccess, setLocalAccess] = useState<Record<string, string[]>>({})
  useEffect(() => {
    if (menuCfg?.access) setLocalAccess(menuCfg.access)
  }, [menuCfg])

  const saveMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${selectedCompanyId}/menu-access`, { access: localAccess }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['menu-access'] }); alert('บันทึกเรียบร้อย') },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const toggle = (role: string, menuKey: string) => {
    setLocalAccess(prev => {
      const current = prev[role] ?? ALL_MENUS.map(m => m.key)
      const next = current.includes(menuKey)
        ? current.filter(k => k !== menuKey)
        : [...current, menuKey]
      return { ...prev, [role]: next }
    })
  }

  const isChecked = (role: string, menuKey: string) => {
    const roleMenus = localAccess[role] ?? ALL_MENUS.map(m => m.key)
    return roleMenus.includes(menuKey)
  }

  // ── Asset management permission (create/edit/delete asset, MA, components) ──
  const { data: assetPermCfg, isLoading: assetPermLoading } = useQuery<{ configurableRoles: string[]; roles: string[] }>({
    queryKey: ['asset-permissions', selectedCompanyId],
    queryFn: () => api.get(`/settings/companies/${selectedCompanyId}/asset-permissions`).then(r => r.data),
    enabled: !!selectedCompanyId,
  })

  const [localAssetRoles, setLocalAssetRoles] = useState<string[]>([])
  useEffect(() => {
    if (assetPermCfg?.roles) setLocalAssetRoles(assetPermCfg.roles)
  }, [assetPermCfg])

  const saveAssetPermMut = useMutation({
    mutationFn: () => api.put(`/settings/companies/${selectedCompanyId}/asset-permissions`, { roles: localAssetRoles }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['asset-permissions'] }); alert('บันทึกเรียบร้อย') },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const toggleAssetRole = (role: string) => {
    setLocalAssetRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role])
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
        ADMIN และ SUPER_ADMIN เห็นเมนูทั้งหมดเสมอ — กำหนดได้เฉพาะ VIEWER และ TECHNICIAN
      </div>

      {companies.length > 1 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">บริษัท</label>
          <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><RefreshCw size={18} className="animate-spin text-gray-300"/></div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">เมนู</th>
                {CONFIGURABLE_ROLES.map(role => (
                  <th key={role} className="px-5 py-3 font-semibold text-gray-600 text-center w-32">{role}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ALL_MENUS.map(menu => (
                <tr key={menu.key} className="hover:bg-gray-50/50">
                  <td className="px-5 py-3 font-medium text-gray-800">{menu.label}</td>
                  {CONFIGURABLE_ROLES.map(role => (
                    <td key={role} className="px-5 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isChecked(role, menu.key)}
                        onChange={() => toggle(role, menu.key)}
                        className="w-4 h-4 rounded accent-blue-600 cursor-pointer"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !selectedCompanyId}
        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
        {saveMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกการตั้งค่าเมนู
      </button>

      {/* Asset management permission */}
      <div className="border-t border-gray-100 pt-6 space-y-3">
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
            <ShieldCheck size={12}/> สิทธิ์การจัดการ Asset
          </h4>
          <p className="text-xs text-gray-400">
            ADMIN และ SUPER_ADMIN จัดการ Asset ได้เสมอ (สร้าง/แก้ไข/ลบ Asset, MA, Components) — เลือก Role อื่นที่ต้องการให้มีสิทธิ์เท่ากัน
          </p>
        </div>

        {assetPermLoading ? (
          <div className="flex justify-center py-6"><RefreshCw size={18} className="animate-spin text-gray-300"/></div>
        ) : (
          <div className="space-y-2">
            {(assetPermCfg?.configurableRoles ?? []).map(role => (
              <label key={role} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                <p className="text-sm font-medium text-gray-800">{role}</p>
                <button
                  type="button"
                  onClick={() => toggleAssetRole(role)}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${localAssetRoles.includes(role) ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${localAssetRoles.includes(role) ? 'translate-x-5' : ''}`}/>
                </button>
              </label>
            ))}
          </div>
        )}

        <button onClick={() => saveAssetPermMut.mutate()} disabled={saveAssetPermMut.isPending || !selectedCompanyId}
          className="flex items-center gap-2 px-5 py-2.5 bg-slate-700 text-white rounded-xl text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
          {saveAssetPermMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกสิทธิ์การจัดการ Asset
        </button>
      </div>
    </div>
  )
}

// ─── Changelog Tab ────────────────────────────────────────────────────────────
function fmtChangelogDate(iso: string) {
  return new Date(iso).toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Renders `code` spans inside a changelog bullet as inline code
function withInlineCode(text: string) {
  return text.split('`').map((part, i) =>
    i % 2 === 1
      ? <code key={i} className="px-1 py-0.5 bg-gray-100 text-gray-700 rounded font-mono text-[12px]">{part}</code>
      : part
  )
}

function ChangelogTab() {
  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      <div>
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <ClipboardCheck size={16} className="text-gray-700"/> ประวัติการเปลี่ยนแปลง (Changelog)
        </h2>
        <p className="text-sm text-gray-400 mt-0.5">ดูประวัติอัพเดตและเปลี่ยนแปลงของระบบ</p>
      </div>

      <div className="space-y-4">
        {CHANGELOG.map(entry => (
          <div key={entry.version} className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`flex items-center justify-between px-5 py-3 ${entry.current ? 'bg-blue-600' : 'bg-gray-900'}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white bg-white/20 px-2.5 py-1 rounded-full">v{entry.version}</span>
                {entry.current && (
                  <span className="text-xs font-semibold text-blue-700 bg-white px-2.5 py-1 rounded-full">Current</span>
                )}
              </div>
              <span className="text-xs text-white/80">{fmtChangelogDate(entry.date)}</span>
            </div>
            <div className="bg-white px-5 py-4 space-y-2.5">
              {entry.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0"/>
                  <p className="text-sm text-gray-700 leading-relaxed">{withInlineCode(item)}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Backup / Restore Tab ───────────────────────────────────────────────────
const icRed = 'w-full border border-red-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white font-mono'

// axios errors from the nginx layer (413 body-too-large, 504 timeout, etc.) come back as
// HTML/plain-text, not JSON — and when the request used responseType:'blob' (export), even a
// genuine JSON error from our own backend arrives as a Blob, not a parsed object. Handle both.
async function describeBackupError(e: any): Promise<string> {
  let msg: string | undefined
  const data = e?.response?.data
  if (data instanceof Blob) {
    try { msg = JSON.parse(await data.text())?.message } catch { /* not JSON (e.g. nginx HTML page) */ }
  } else if (data && typeof data === 'object') {
    msg = data.message
  }
  if (msg) return msg
  const status = e?.response?.status
  if (status === 413) return 'ไฟล์มีขนาดใหญ่เกินกว่าที่เซิร์ฟเวอร์รับได้ (413)'
  if (status === 504 || status === 502) return 'เซิร์ฟเวอร์ใช้เวลาประมวลผลนานเกินไป (timeout) — ไฟล์อาจใหญ่หรือข้อมูลมากเกินไป'
  if (status) return `เกิดข้อผิดพลาด (HTTP ${status})`
  return e?.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ (การเชื่อมต่อล้มเหลว)'
}

function BackupTab() {
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [restoring, setRestoring] = useState(false)
  const [restoreResult, setRestoreResult] = useState<{ ok: boolean; message: string } | null>(null)

  const handleExport = async () => {
    setExporting(true); setExportError(null)
    try {
      const res = await api.get('/backup/export', { responseType: 'blob' })
      const cd = res.headers['content-disposition'] as string | undefined
      const match = cd?.match(/filename="?([^"]+)"?/)
      const filename = match?.[1] || `backup-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setExportError(await describeBackupError(e))
    } finally { setExporting(false) }
  }

  const handleRestore = async () => {
    if (!file || confirmText !== 'RESTORE') return
    if (!window.confirm('ยืนยันการกู้คืนข้อมูล? ข้อมูลปัจจุบันทั้งหมดในระบบจะถูกลบและแทนที่ด้วยข้อมูลจากไฟล์ backup การกระทำนี้ย้อนกลับไม่ได้')) return
    setRestoring(true); setRestoreResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('confirmPhrase', confirmText)
      const res = await api.post('/backup/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      setRestoreResult({ ok: true, message: `${res.data.message} (ไฟล์แนบที่กู้คืน: ${res.data.restoredFiles} รายการ)` })
      setFile(null); setConfirmText('')
    } catch (e: any) {
      setRestoreResult({ ok: false, message: await describeBackupError(e) })
    } finally { setRestoring(false) }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Export */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <HardDriveDownload size={16} className="text-blue-600"/>
          <h3 className="font-semibold text-gray-900">สำรองข้อมูล (Export Backup)</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          ดาวน์โหลดไฟล์ .zip ที่รวมข้อมูลทั้งหมดในระบบ (ทุกบริษัท: Asset, Ticket, User, Software License ฯลฯ) พร้อมไฟล์แนบทั้งหมด
        </p>
        <button onClick={handleExport} disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {exporting ? <RefreshCw size={14} className="animate-spin"/> : <HardDriveDownload size={14}/>}
          {exporting ? 'กำลังสร้างไฟล์...' : 'Export Backup'}
        </button>
        {exportError && <p className="text-xs text-red-600 mt-2 flex items-center gap-1.5"><XCircle size={13}/>{exportError}</p>}
      </div>

      {/* Restore */}
      <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <HardDriveUpload size={16} className="text-red-600"/>
          <h3 className="font-semibold text-gray-900">กู้คืนข้อมูล (Restore Backup)</h3>
        </div>
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-700 mb-4">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5"/>
          <span>
            การกู้คืนจะ<strong>ลบข้อมูลปัจจุบันทั้งหมดในระบบ</strong> (ทุกบริษัท ทุก Asset, Ticket, User, ไฟล์แนบ ฯลฯ)
            แล้วแทนที่ด้วยข้อมูลจากไฟล์ backup — <strong>ไม่สามารถย้อนกลับได้</strong> กรุณา Export สำรองไว้ก่อนทำรายการ
          </span>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
              <FileArchive size={12}/> ไฟล์ Backup (.zip)
            </label>
            <input type="file" accept=".zip"
              onChange={e => { setFile(e.target.files?.[0] || null); setRestoreResult(null) }}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 file:text-sm file:font-medium hover:file:bg-gray-200"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              พิมพ์ <span className="font-mono font-bold text-red-600">RESTORE</span> เพื่อยืนยัน
            </label>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)} className={icRed} placeholder="RESTORE"/>
          </div>
          <button onClick={handleRestore} disabled={restoring || !file || confirmText !== 'RESTORE'}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-40">
            {restoring ? <RefreshCw size={14} className="animate-spin"/> : <HardDriveUpload size={14}/>}
            {restoring ? 'กำลังกู้คืนข้อมูล...' : 'Restore Backup'}
          </button>
          {restoreResult && (
            <p className={`text-xs flex items-center gap-1.5 ${restoreResult.ok ? 'text-green-600' : 'text-red-600'}`}>
              {restoreResult.ok ? <CheckCircle size={13}/> : <XCircle size={13}/>} {restoreResult.message}
            </p>
          )}
          {restoreResult?.ok && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5">
              <AlertTriangle size={13}/> แนะนำให้ออกจากระบบแล้วเข้าสู่ระบบใหม่
            </p>
          )}
        </div>
      </div>

      <OneDriveSyncCard/>
    </div>
  )
}

// ─── OneDrive Sync Card ──────────────────────────────────────────────────────
interface OneDriveConfig {
  tenantId: string
  clientId: string
  clientSecretSet: boolean
  driveUser: string
  folderPath: string
  keepCount: number
  sync: { enabled: boolean; intervalHours: number; syncTime: string; lastRun: string | null; lastResult: string | null; nextRun: string | null }
}

function OneDriveSyncCard() {
  const qc = useQueryClient()
  const [showSecret, setShowSecret] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)

  const { data: cfg } = useQuery<OneDriveConfig>({
    queryKey: ['onedrive-config'],
    queryFn: () => api.get('/backup/onedrive').then(r => r.data),
  })

  const [connForm, setConnForm] = useState({ tenantId: '', clientId: '', clientSecret: '', driveUser: '', folderPath: 'IT-Management-Backups', keepCount: 30 })
  const [syncForm, setSyncForm] = useState({ enabled: false, intervalHours: 24, syncTime: '02:00' })

  useEffect(() => {
    if (!cfg) return
    setConnForm({ tenantId: cfg.tenantId, clientId: cfg.clientId, clientSecret: '', driveUser: cfg.driveUser, folderPath: cfg.folderPath || 'IT-Management-Backups', keepCount: cfg.keepCount || 30 })
    setSyncForm({ enabled: cfg.sync.enabled, intervalHours: cfg.sync.intervalHours, syncTime: cfg.sync.syncTime || '02:00' })
  }, [cfg])

  const configured = !!(cfg?.tenantId && cfg?.clientId && cfg?.clientSecretSet && cfg?.driveUser)

  const saveConnMut = useMutation({
    mutationFn: () => api.put('/backup/onedrive', {
      tenantId: connForm.tenantId,
      clientId: connForm.clientId,
      clientSecret: connForm.clientSecret || undefined,
      driveUser: connForm.driveUser,
      folderPath: connForm.folderPath,
      keepCount: connForm.keepCount,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onedrive-config'] }); setTestResult(null); setConnForm(f => ({ ...f, clientSecret: '' })) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const saveSyncMut = useMutation({
    mutationFn: () => api.put('/backup/onedrive/sync', { ...syncForm, timezoneOffsetMinutes: new Date().getTimezoneOffset() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onedrive-config'] }),
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const handleTest = async () => {
    setTesting(true); setTestResult(null)
    try {
      const res = await api.post('/backup/onedrive/test', {})
      setTestResult({ ok: true, message: res.data.message })
    } catch (e: any) {
      setTestResult({ ok: false, message: e.response?.data?.message || 'ไม่สามารถเชื่อมต่อได้' })
    } finally { setTesting(false) }
  }

  const handleSyncNow = async () => {
    setSyncing(true); setSyncResult(null)
    try {
      const res = await api.post('/backup/onedrive/sync/run', {})
      setSyncResult(res.data.message)
      qc.invalidateQueries({ queryKey: ['onedrive-config'] })
    } catch (e: any) {
      setSyncResult(`ผิดพลาด: ${e.response?.data?.message || e.message}`)
    } finally { setSyncing(false) }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1">
        <Cloud size={16} className="text-indigo-600"/>
        <h3 className="font-semibold text-gray-900">Sync Backup ไป OneDrive</h3>
        {cfg?.sync.enabled && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium flex items-center gap-1">
            <Clock size={10}/> Auto Sync
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        อัปโหลดไฟล์ backup ขึ้น OneDrive ของบัญชีที่กำหนด ผ่าน Microsoft Graph API โดยอัตโนมัติตามรอบเวลา
      </p>

      <div className="space-y-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">การเชื่อมต่อ</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tenant ID</label>
            <input value={connForm.tenantId} onChange={e => setConnForm(f => ({ ...f, tenantId: e.target.value }))} className={ic} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Client ID</label>
            <input value={connForm.clientId} onChange={e => setConnForm(f => ({ ...f, clientId: e.target.value }))} className={ic} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Client Secret
            {cfg?.clientSecretSet && <span className="ml-1.5 text-green-600 font-normal">● บันทึกไว้แล้ว</span>}
          </label>
          <div className="relative">
            <input value={connForm.clientSecret} onChange={e => setConnForm(f => ({ ...f, clientSecret: e.target.value }))}
              type={showSecret ? 'text' : 'password'} className={ic + ' pr-10'}
              placeholder={cfg?.clientSecretSet ? '••••••••  (เว้นว่างถ้าไม่เปลี่ยน)' : 'Secret Value จาก Azure Portal'}/>
            <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showSecret ? <EyeOff size={15}/> : <Eye size={15}/>}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            App registration ต้องมีสิทธิ์ Application permission <span className="font-mono">Files.ReadWrite.All</span> และผ่าน admin consent แล้ว
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">บัญชี OneDrive (อีเมล)</label>
            <input value={connForm.driveUser} onChange={e => setConnForm(f => ({ ...f, driveUser: e.target.value }))} className={ic} placeholder="it@yourcompany.com"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">โฟลเดอร์ปลายทาง</label>
            <input value={connForm.folderPath} onChange={e => setConnForm(f => ({ ...f, folderPath: e.target.value }))} className={ic} placeholder="IT-Management-Backups"/>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">เก็บไฟล์ backup ล่าสุดจำนวน (ไฟล์)</label>
          <input type="number" min={1} value={connForm.keepCount} onChange={e => setConnForm(f => ({ ...f, keepCount: parseInt(e.target.value) || 1 }))} className={ic + ' w-32'}/>
          <p className="text-xs text-gray-400 mt-1">ไฟล์ backup เก่าเกินจำนวนนี้จะถูกลบออกจาก OneDrive อัตโนมัติหลัง sync แต่ละครั้ง</p>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <button onClick={() => saveConnMut.mutate()} disabled={saveConnMut.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saveConnMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึก
          </button>
          <button onClick={handleTest} disabled={testing || !configured}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
            {testing ? <RefreshCw size={13} className="animate-spin"/> : <Cloud size={13}/>} ทดสอบ
          </button>
          {testResult && (
            <span className={`flex items-center gap-1.5 text-xs font-medium ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>
              {testResult.ok ? <CheckCircle size={13}/> : <XCircle size={13}/>} {testResult.message}
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 mt-5 pt-4 space-y-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
          <Calendar size={12}/> ตั้งเวลา Sync อัตโนมัติ
        </h4>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
          <div>
            <p className="text-sm font-medium text-gray-800">เปิดใช้งาน sync อัตโนมัติ</p>
            <p className="text-xs text-gray-400 mt-0.5">สร้าง backup และอัปโหลดขึ้น OneDrive ตามรอบที่กำหนด</p>
          </div>
          <button onClick={() => setSyncForm(f => ({ ...f, enabled: !f.enabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors ${syncForm.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${syncForm.enabled ? 'translate-x-5' : ''}`}/>
          </button>
        </div>

        {syncForm.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">รอบการทำงาน</label>
              <select value={syncForm.intervalHours} onChange={e => setSyncForm(f => ({ ...f, intervalHours: parseInt(e.target.value) }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                {INTERVAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                เวลาที่รัน
                {syncForm.intervalHours < 24 && <span className="ml-1 text-gray-400 font-normal">(ใช้กับ ≥ 1 วัน)</span>}
              </label>
              <input type="time" value={syncForm.syncTime}
                onChange={e => setSyncForm(f => ({ ...f, syncTime: e.target.value }))}
                disabled={syncForm.intervalHours < 24}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-40 disabled:cursor-not-allowed"/>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button onClick={() => saveSyncMut.mutate()} disabled={saveSyncMut.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {saveSyncMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} บันทึกตาราง
          </button>
          <button onClick={handleSyncNow} disabled={syncing || !configured}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
            {syncing ? <RefreshCw size={13} className="animate-spin"/> : <Play size={13}/>} Sync ตอนนี้
          </button>
        </div>

        {syncResult && (
          <p className={`text-xs px-3 py-2 rounded-lg ${syncResult.startsWith('ผิดพลาด') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {syncResult}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-gray-400 mb-0.5">Sync ล่าสุด</p>
            <p className="font-medium text-gray-700">{fmtDate(cfg?.sync.lastRun ?? null)}</p>
            {cfg?.sync.lastResult && <p className="text-gray-400 mt-0.5 truncate">{cfg.sync.lastResult}</p>}
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-gray-400 mb-0.5">Sync ครั้งถัดไป</p>
            <p className="font-medium text-gray-700">{cfg?.sync.enabled ? fmtDate(cfg.sync.nextRun) : 'ปิดใช้งาน'}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
type Tab = 'general' | 'masterdata' | 'import' | 'manual' | 'servicedesk' | 'menu-access' | 'changelog' | 'backup'

const BASE_TABS: { key: Tab; label: string; icon: any }[] = [
  { key: 'masterdata',   label: 'Master Data',        icon: Database     },
  { key: 'import',       label: 'Import Asset',       icon: Upload       },
  { key: 'servicedesk',  label: 'ServiceDesk',        icon: Headphones   },
  { key: 'menu-access',  label: 'Permission',         icon: ShieldCheck  },
  { key: 'changelog',    label: 'Changelog',          icon: History  },
  { key: 'general',      label: 'System',             icon: Settings     },
  { key: 'manual',       label: 'Manual',             icon: BookOpen     },
]

export default function SettingsPage() {
  const [tab, setTab] = useState<Tab>('masterdata')

  const isSuperAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null')?.role === 'SUPER_ADMIN' } catch { return false }
  })()

  const TABS = isSuperAdmin
    ? [...BASE_TABS, { key: 'backup' as Tab, label: 'Backup', icon: DatabaseBackup }]
    : BASE_TABS

  const { data: companies = [], isLoading } = useQuery<CompanyConfig[]>({
    queryKey: ['settings-companies'],
    queryFn: () => api.get('/settings/companies').then(r => r.data),
    enabled: tab === 'general' || tab === 'menu-access',
  })

  return (
    <div className="min-h-full">
      <div className="bg-white border-b border-gray-100 px-6 py-5">
        <div className="flex items-center gap-3 max-w-5xl mx-auto">
          <div className="w-10 h-10 rounded-2xl bg-gray-900 flex items-center justify-center">
            <Settings size={20} className="text-white"/>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">ตั้งค่าระบบ</h1>
            <p className="text-sm text-gray-500">จัดการการตั้งค่าของระบบ IT Management</p>
          </div>
        </div>
        <div className="flex gap-1 mt-5 max-w-5xl mx-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}>
              <t.icon size={15}/> {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'general' && (
        <div className="p-6 max-w-3xl mx-auto space-y-6">
          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">การเชื่อมต่อภายนอก</h2>
            {isLoading && (
              <div className="flex justify-center py-8 text-gray-300"><RefreshCw size={18} className="animate-spin"/></div>
            )}
            {!isLoading && companies.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-8 border-2 border-dashed border-gray-100 rounded-2xl">
                ยังไม่มีข้อมูล Company — เพิ่มได้ที่ Master Data
              </div>
            )}
            <div className="space-y-3">
              {companies.map(c => <CompanyConnectionCard key={c.id} company={c}/>)}
            </div>
          </section>

          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">ข้อมูลระบบ</h2>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-50">
              {[['เวอร์ชัน',APP_VERSION],['ฐานข้อมูล','PostgreSQL'],['Backend','Node.js + Express + Prisma'],['Frontend','React + Vite + Tailwind CSS']].map(([l,v])=>(
                <div key={l} className="flex items-center justify-between px-5 py-3">
                  <span className="text-sm text-gray-500">{l}</span>
                  <span className="text-sm font-medium text-gray-900">{v}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'masterdata'   && <MasterDataPage isAdmin={true}/>}
      {tab === 'import'       && <ImportPage/>}
      {tab === 'servicedesk'  && <ServiceDeskSettingsPage/>}
      {tab === 'menu-access'  && <MenuAccessTab/>}
      {tab === 'changelog'    && <ChangelogTab/>}
      {tab === 'backup' && isSuperAdmin && <BackupTab/>}
      {tab === 'manual'       && (
        <div className="flex-1 min-h-0 overflow-hidden" style={{ height: 'calc(100vh - 130px)' }}>
          <ManualTab/>
        </div>
      )}
    </div>
  )
}
